/* Files out of the mini app (DESIGN47).

   Inside Telegram's webview a fetch()ed blob cannot be saved: <a download>
   is silently inert. What works is handing Telegram a plain HTTPS URL —
   its own downloader (WebApp.downloadFile, Bot API 8.0+) or the system
   browser (openLink). Neither carries our Bearer, so the app first asks the
   server for a SHORT-LIVED SIGNED LINK and passes that on. */

import { downloadsApi, downloadUrl, DownloadKind } from '../architect/api';
import { getWebApp } from './telegram';
import { getSessionToken } from '../auth/sessionToken';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

export type Delivery = 'telegram' | 'browser' | 'declined';

/** Save a document: Telegram's downloader when the client has it (the
    answer waits for the user's yes/no in Telegram's own prompt), otherwise
    the system browser (which downloads an attachment on its own). */
export async function deliverFile(kind: DownloadKind, id: string): Promise<Delivery> {
  const signed = await downloadsApi.sign(kind, id, false);
  const url = downloadUrl(signed);
  const app = getWebApp();
  if (app && app.downloadFile && app.isVersionAtLeast && app.isVersionAtLeast('8.0')) {
    try {
      const accepted = await new Promise<boolean>((resolve) => {
        let done = false;
        const finish = (ok: boolean) => { if (!done) { done = true; resolve(ok); } };
        app.downloadFile!({ url, file_name: signed.filename }, finish);
        // A client that never calls back (older builds) — assume it took it.
        window.setTimeout(() => finish(true), 60000);
      });
      return accepted ? 'telegram' : 'declined';
    } catch {
      /* an older client that lies about its version — fall through */
    }
  }
  openOutside(url);
  return 'browser';
}

/** Open a PDF to READ (inline) in the system browser. */
export async function openFile(kind: DownloadKind, id: string): Promise<void> {
  const signed = await downloadsApi.sign(kind, id, true);
  openOutside(downloadUrl(signed));
}

/** The full web app, on this project, straight into a panel — signed in as
    the same person. The URL carries a 60-second SINGLE-USE handoff code
    (never the session token itself: a browser keeps URLs in its history);
    the web redeems it for a session of its own. The web is served from the
    mini app's own origin (/tma is a path on it). */
export async function openInWeb(projectId: string, open?: 'poster' | 'estimate' | 'pricing' | 'proposal' | 'comments'): Promise<void> {
  const params = new URLSearchParams({ project: projectId });
  if (open) params.set('open', open);
  const token = getSessionToken();
  let hash = '';
  if (token) {
    try {
      const r = await fetch(API + '/api/auth/handoff', { method: 'POST', headers: { Authorization: 'Bearer ' + token } });
      if (r.ok) {
        const data = await r.json();
        if (data && typeof data.code === 'string') hash = '#handoff=' + encodeURIComponent(data.code);
      }
    } catch { /* no handoff — the web will show its login */ }
  }
  openOutside(window.location.origin + '/?' + params.toString() + hash);
}

function openOutside(url: string): void {
  const app = getWebApp();
  if (app && app.openLink) {
    try { app.openLink(url); return; } catch { /* fall through */ }
  }
  window.open(url, '_blank', 'noopener');
}
